<template>
  <div class="share-flow-modal">
    <CustomModal @onClose="close">
      <div
        class="share-flow-modal-content-wrapper"
        :class="customClass"
        ref="shareModal"
      >
        <div class="content">
          <div>
            <h3 class="content-title">Flow Teilen</h3>
            <p class="content-paragraph">
              Teilen Sie Ihren Flow mit anderen Personen.
            </p>
          </div>

          <form @submit.prevent="save()">
            <div class="input-field">
              <input
                class="email-input"
                placeholder="E-Mail-Adresse"
                v-model="shareParams.email"
                type="email"
                required
              />
              <CustomDropdown className="permission-selector form-input">
                <template v-slot:selected-option>
                  <span class="line-clamp"
                    >{{
                      getShareParamsAccessLevelName(shareParams.accessLevel)
                    }}
                  </span>
                </template>
                <template v-slot:option-data>
                  <b-dropdown-item
                    v-for="(level, index) in getDropDownAccessLevels"
                    :key="level.name"
                    @click="setShareParamsAccessLevel(level.name)"
                    :active="shareParams.accessLevel === level.name"
                  >
                    <template v-if="index !== 0">
                      {{ level.caption }}
                    </template>
                    <template v-if="index === 0">
                      <div class="d-flex justify-content-between w-100">
                        <span>{{ level.caption }}</span>

                        <span>
                          <img
                            class="button-icon"
                            :src="require('../../assets/img/chevron-down.svg')"
                        /></span>
                      </div>
                    </template>
                  </b-dropdown-item>
                </template>
              </CustomDropdown>
            </div>
            <div class="form-element-wrapper">
              <CommonButton
                class="common-button-component"
                :isLoading="isLoadingShareFlowButton"
                :isDisabled="isLoadingShareFlowButton"
              >
                Teilen
              </CommonButton>
            </div>
          </form>
          <div class="flow-share-contact-info-wrap" v-if="!flows.length">
            <template v-if="!isLoadingFlowPolicies">
              <WorkflowSharePoliciesAccordion
                heading="Benutzer mit Zugriff"
                :policies="getAcceptedPolicies"
                :loadingId="shareLoadingId"
                :getDropDownAccessLevels="getDropDownAccessLevels"
                :getShareParamsAccessLevelName="getShareParamsAccessLevelName"
                :isCurrentUser="isCurrentUser"
                @onDropdownOpen="onDropdownOpen"
                @onDropdownClose="onDropdownClose"
                @removePolicy="removePolicy"
                @changePolicy="changePolicy"
              />
              <WorkflowSharePoliciesAccordion
                v-if="getPendingPolicies.length"
                heading="Offene Anfragen"
                :policies="getPendingPolicies"
                :loadingId="shareLoadingId"
                :getDropDownAccessLevels="getDropDownAccessLevels"
                :getShareParamsAccessLevelName="getShareParamsAccessLevelName"
                :isCurrentUser="isCurrentUser"
                @onDropdownOpen="onDropdownOpen"
                @onDropdownClose="onDropdownClose"
                @removePolicy="removePolicy"
                @changePolicy="changePolicy"
              />
            </template>

            <div v-if="isLoadingFlowPolicies" class="loading-wrapper">
              <LoadingSpinner />
            </div>
          </div>
        </div>
      </div>
    </CustomModal>
  </div>
</template>

<script>
import { workFlowAccessLevels } from '../../enum/auth.enum';
import {
  getWorkFlowAccessRights,
  shareWorkFlow,
} from '../../services/workflow.service';

import CustomDropdown from '../widgets/CustomDropdown.vue';
import CustomModal from './CustomModal.vue';
import CommonButton from '../widgets/CommonButton.vue';
import LoadingSpinner from '../widgets/LoadingSpinner.vue';

import { deleteWorkflowPolicy } from '@/services/axios/data.api';
import WorkflowSharePoliciesAccordion from '@/components/WorkFlow/WorkflowSharePoliciesAccordion.vue';
export default {
  name: 'ShareFlowModal',
  data() {
    return {
      accessLevels: workFlowAccessLevels,
      shareParams: {
        email: null,
        accessLevel: 'execute',
      },
      flowPolicies: [],
      isLoadingFlowPolicies: false,
      isLoadingShareFlowButton: false,
      shareLoadingId: null,
    };
  },
  props: {
    flowId: {
      type: Number,
      required: false,
    },
    flows: {
      type: Array,
      required: false,
      default: () => [],
    },
    customClass: {
      type: String,
      required: false,
      default: '',
    },
  },
  methods: {
    close: function () {
      this.$emit('close');
    },
    save: async function () {
      try {
        this.isLoadingShareFlowButton = true;
        await this.shareFlow({
          recipient: this.shareParams.email,
          access_level: this.shareParams.accessLevel,
        });
        this.$emit('save');
        this.$set(this.shareParams, 'email', null);
        this.showToaster(
          'Workflow wurde erfolgreich geteilt',
          'Erfolg',
          'success'
        );
      } catch (error) {
        this.showToaster(error.message, 'Fehlerhaft !', 'danger');
      } finally {
        this.isLoadingShareFlowButton = false;
      }
    },
    async shareFlow({ recipient, access_level }) {
      try {
        if (!this.flows.length) {
          const payload = {
            recipient,
            flow_id: this.flowId,
            access_level,
          };
          const response = await shareWorkFlow(payload, true);

          if (response && response.status === 200 && response.data) {
            // Refresh the list of users with access rights
            this.getAccessRights();
          }
        } else {
          await Promise.allSettled(
            this.flows.map(async (flow) => {
              const payload = {
                recipient,
                flow_id: flow.id,
                access_level,
              };
              return await shareWorkFlow(payload, true);
            })
          );
        }
      } catch (error) {
        if (error && error.status === 400 && error.data && error.data.message) {
          let message = error.data.message;
          if (message.includes('The Access of the owner cannot be changed')) {
            this.showToaster(
              'Dieser User hat den Flow erstellt, du kannst ihn nicht einladen',
              'Fehlerhaft !',
              'danger'
            );
          } else if (message.includes('E-mail not found')) {
            this.showToaster(
              'Die angegebene Email-Adresse konnte nicht gefunden werden.',
              'Fehlerhaft !',
              'danger'
            );
          }
        } else if (error.status === 404) {
          this.showToaster(
            'Die angegebene Email-Adresse konnte nicht gefunden werden.',
            'Fehlerhaft !',
            'danger'
          );
        }
        throw error;
      }
    },
    async getAccessRights() {
      try {
        const response = await getWorkFlowAccessRights(this.flowId);

        if (
          response &&
          response.status === 200 &&
          response.data &&
          response.data.length > 0
        ) {
          this.flowPolicies = response.data;
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log({ error });
      }
    },
    async removePolicy(p) {
      try {
        this.shareLoadingId = p.id;
        const payload = {
          policy_id: p.id,
          flow_id: p.resource,
        };
        const response = await deleteWorkflowPolicy(payload);
        if (response && response.status === 200 && response.data) {
          await this.getAccessRights();
        }
        this.showToaster(
          'Die Berechtigung wurde erfolgreich entfernt',
          'Erfolg',
          'success'
        );
      } catch (error) {
        //
      } finally {
        this.shareLoadingId = null;
      }
    },
    changePolicy: async function (p, accessName) {
      try {
        this.shareLoadingId = p.id;
        await this.shareFlow({ recipient: p.email, access_level: accessName });
        this.showToaster(
          'Die Berechtigung wurde erfolgreich angepasst',
          'Erfolg',
          'success'
        );
      } catch (error) {
        this.showToaster(error.message, 'Fehlerhaft !', 'danger');
      } finally {
        this.shareLoadingId = null;
      }
    },
    setShareParamsAccessLevel(accessName) {
      this.shareParams.accessLevel = accessName;
    },
    getShareParamsAccessLevelName(accessLevel) {
      return this.accessLevels.find((level) => level.name == accessLevel)
        .caption;
    },
    isCurrentUser(userEmail) {
      return this.user.attributes.email === userEmail;
    },
    async loadFlowPolicies() {
      try {
        if (!this.flows.length) {
          this.isLoadingFlowPolicies = true;
          await this.getAccessRights();
        }
      } catch (error) {
        //
      } finally {
        this.isLoadingFlowPolicies = false;
      }
    },

    onDropdownOpen() {
      this.isUserIdentityDropdownOpen = true;
    },
    onDropdownClose() {
      this.isUserIdentityDropdownOpen = false;
    },
    showToaster(message, title, variant) {
      this.$bvToast.toast(message, {
        title,
        variant,
      });
    },
  },
  created() {
    this.loadFlowPolicies();
  },
  computed: {
    getDropDownAccessLevels() {
      return this.accessLevels.filter((el) => el.name !== 'owner');
    },
    getAcceptedPolicies() {
      return this.getSortedFlowPolicies.filter(
        (policy) =>
          policy.status === 'accepted' ||
          !policy.status ||
          policy.identity === 'public' // for the flow created before accept or reject functionality
      );
    },
    getPendingPolicies() {
      return this.getSortedFlowPolicies.filter(
        (policy) => policy.status === 'pending' && policy.identity !== 'public'
      );
    },
    getRejectedPolicies() {
      return this.getSortedFlowPolicies.filter(
        (policy) => policy.status === 'rejected'
      );
    },
    getSortedFlowPolicies() {
      return [...this.flowPolicies].sort((a, b) => {
        if (a.access_level === 'owner') return -1;
        if (b.access_level === 'owner') return 1;
        return 0;
      });
    },
    user() {
      return this.$store.state.user;
    },
  },
  components: {
    CustomModal,
    CommonButton,
    LoadingSpinner,
    CustomDropdown,
    WorkflowSharePoliciesAccordion,
  },
};
</script>

<style scoped>
.share-flow-modal .share-flow-modal-content-wrapper {
  height: 554px;
  max-width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding-inline: 98px;
  padding-block: 70px;
}
@media (max-width: 767px) {
  .share-flow-modal .share-flow-modal-content-wrapper {
    padding-inline: 40px;
  }
}
.share-flow-modal .content {
  width: 100%;
  height: fit-content;
}

.share-flow-modal .content-title {
  font-size: 20px;
  color: #333343;
  margin: 0;
  font-weight: 400;
  text-transform: uppercase;
  padding: 0 6px;
}

.share-flow-modal .content-paragraph {
  font-size: 16px;
  display: flex;
  align-items: center;
  gap: 6px;
  color: #333343;
  margin: 20px 0;
  padding: 0 6px;
}

.share-flow-modal .email-input {
  outline: none;
  border: 0.5px solid #333343;
  border-radius: 4px;
  width: 330px;
  height: 34px;
  font-size: 16px;
  padding: 4px 16px;
  font-weight: 400;
  line-height: 20px;
  color: rgba(51, 51, 51);
}

@media (max-width: 767px) {
  .share-flow-modal .email-input {
    width: 100%;
    max-width: 100%;
  }
}
.share-flow-modal .email-input::placeholder {
  color: rgba(51, 51, 51, 0.24);
}
.share-flow-modal .form-element-wrapper {
  padding-top: 36px;
  position: relative;
}
.share-flow-modal .custom-dropdown.permission-selector {
  border-radius: 4px;
  font-size: 13px;
  width: 158px;
  display: flex;
  height: 100%;
}
.share-flow-modal
  ::v-deep
  .custom-dropdown.permission-selector.form-input
  .custom-dropdown-toggle {
  height: 34px;
}

.share-flow-modal .user-access-desc {
  font-size: 16px;
  color: #333343;
  padding: 0 6px;
}

.share-flow-modal .flow-share-contact-info {
  height: 150px;
  overflow: auto;
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.flow-share-contact-info-wrap {
  margin-top: 27px;
  height: 187px;
  overflow: scroll;
  display: flex;
  flex-direction: column;
  gap: 26px;
}

::v-deep .dropdown-item.active {
  background: #e5e5e591;
}

.share-flow-modal .loading-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}
.share-flow-modal ::v-deep .loading-wrapper .progress-icon-wrapper {
  position: unset;
}

.share-flow-modal
  ::v-deep
  .permission-selector.form-input
  .custom-dropdown-toggle
  img {
  margin-top: 3px;
}

.share-flow-modal ::v-deep .permission-selector.active .button-icon {
  transform: rotate(180deg);
  width: 9px;
}

.share-flow-modal .input-field {
  display: flex;
  align-items: center;
  gap: 8px;
}

@media (max-width: 767px) {
  .share-flow-modal .input-field {
    flex-direction: column;
    align-items: flex-start;
  }
}
.share-flow-modal form {
  padding: 0 6px;
}

.share-flow-modal .share-flow-modal-content-wrapper.share-multiple-flows {
  height: 355px !important;
}
</style>
